import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { prefersReducedMotion } from '@/lib/store'
import { STATUS_LABEL } from '@/lib/format'

export function CountUp({ to, duration = 1600, format = (v: number) => Math.round(v).toLocaleString('en-US') }: {
  to: number; duration?: number; format?: (v: number) => string
}) {
  const [v, setV] = useState(prefersReducedMotion() ? to : 0)
  const raf = useRef(0)
  useEffect(() => {
    if (prefersReducedMotion()) { setV(to); return }
    const t0 = performance.now()
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / duration)
      setV(to * (1 - Math.pow(1 - k, 3)))
      if (k < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [to, duration])
  return <span className="num">{format(v)}</span>
}

const STATUS_COLOR: Record<string, string> = {
  operating: 'var(--st-operating)', construction: 'var(--st-construction)', permitted: 'var(--st-proposed)', proposed: 'var(--st-proposed)', cancelled: 'var(--st-cancelled)',
}

export function StatusPill({ status, upstream }: { status: string; upstream?: string | null }) {
  const c = STATUS_COLOR[status] ?? 'var(--text-muted)'
  const label = upstream === 'paused' ? 'Paused' : STATUS_LABEL[status] ?? status
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium" style={{ color: `color-mix(in srgb, ${c} 65%, var(--text))`, borderColor: `color-mix(in srgb, ${c} 45%, transparent)` }}>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {label}
    </span>
  )
}

export function statusColor(s: string): string { return STATUS_COLOR[s] ?? '#888' }

/** Transparency Score ring (0–100). Rewards disclosure, not low usage. */
export function ScoreRing({ score, size = 76 }: { score: number | null | undefined; size?: number }) {
  const s = score ?? 0
  const r = size / 2 - 6
  const c = 2 * Math.PI * r
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} role="img" aria-label={`Transparency score ${s} out of 100`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-strong)" strokeWidth={6} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--power)" strokeWidth={6} strokeLinecap="round" strokeDasharray={`${(c * s) / 100} ${c}`} style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
      </svg>
      <div className="absolute text-center leading-none">
        <div className="num font-semibold" style={{ fontSize: Math.round(size * 0.26) }}>{s}</div>
        {/* the "of 100" caption needs ~40px of inner ring; small rings rely on the aria-label */}
        {size >= 72 && <div className="text-[11px] text-faint" style={{ marginTop: size * 0.03 }}>of 100</div>}
      </div>
    </div>
  )
}

export function Spinner({ className }: { className?: string }) {
  return <span aria-hidden className={clsx('inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--hairline-strong)] border-t-[var(--power)]', className)} />
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[60ch] text-sm leading-relaxed text-muted">{children}</div>
}

export function Section({ id, title, children, aside }: { id?: string; title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="mb-3 flex items-end justify-between gap-4">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}
